import React, { useState, useEffect, useRef } from 'react';
import { Encounter, ConsentForm } from '../../types/encounter';
import { API_ENDPOINTS } from '../../config/api';
import { FileText, Upload, Link, PenTool, CheckCircle, Loader2, Trash2, ExternalLink, Copy, AlertCircle, Hash } from 'lucide-react';
import ConfirmDeleteModal from '../common/ConfirmDeleteModal';

interface ConsentFormsSectionProps {
  encounter: Encounter;
  onUpdate: () => void;
}

const ConsentFormsSection: React.FC<ConsentFormsSectionProps> = ({ encounter, onUpdate }) => {
  const [forms, setForms] = useState<ConsentForm[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [fileToDelete, setFileToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [alertMessage, setAlertMessage] = useState<{ title: string; message: string; type: 'success' | 'error' } | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchForms();
  }, [encounter.id]);

  const fetchForms = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.ENCOUNTER_CONSENT.LIST(encounter.id), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setForms(data);
      }
    } catch (error) {
      console.error('Failed to fetch consent forms:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      setUploadError('Only PDF files are allowed');
      return;
    }

    setIsUploading(true);
    setUploadError(null);

    try {
      // 1. Initiate upload
      const initResponse = await fetch(API_ENDPOINTS.ENCOUNTER_CONSENT.INITIATE_UPLOAD, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          encounter_id: encounter.id,
          filename: file.name,
          content_type: file.type,
          size_bytes: file.size
        })
      });

      if (!initResponse.ok) throw new Error('Failed to initiate upload');

      const { upload_url, fields, s3_key, file_id } = await initResponse.json();

      // 2. Upload to S3
      const formData = new FormData();
      Object.entries(fields).forEach(([key, value]) => {
        formData.append(key, value as string);
      });
      formData.append('file', file);

      const uploadResponse = await fetch(upload_url, {
        method: 'POST',
        body: formData
      });

      if (!uploadResponse.ok) throw new Error('Failed to upload to S3');

      // 3. Complete upload
      const completeResponse = await fetch(`${API_ENDPOINTS.ENCOUNTER_CONSENT.COMPLETE_UPLOAD}?encounter_id=${encounter.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          s3_key,
          filename: file.name,
          content_type: file.type,
          size_bytes: file.size,
          file_id
        })
      });

      if (!completeResponse.ok) throw new Error('Failed to complete upload');

      const updatedFiles = await completeResponse.json();
      setForms(updatedFiles);
      onUpdate();
      setAlertMessage({ title: 'Success', message: 'File uploaded successfully', type: 'success' });

    } catch (error) {
      console.error('Upload error:', error);
      setUploadError('Failed to upload file. Please try again.');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleGenerateLink = async (fileId: string) => {
    try {
      const response = await fetch(API_ENDPOINTS.ENCOUNTER_CONSENT.GENERATE_LINK(fileId) + `?encounter_id=${encounter.id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        fetchForms();
        setAlertMessage({ title: 'Success', message: 'Signing link generated', type: 'success' });
      } else {
        setAlertMessage({ title: 'Error', message: 'Failed to generate link', type: 'error' });
      }
    } catch (error) {
      console.error('Error generating link:', error);
      setAlertMessage({ title: 'Error', message: 'An error occurred', type: 'error' });
    }
  };

  const copyLinkToClipboard = (token: string) => {
    const url = `${window.location.origin}/sign-consent/${token}`;
    navigator.clipboard.writeText(url);
    setAlertMessage({ title: 'Copied', message: 'Link copied to clipboard!', type: 'success' });
  };

  const handleDeleteClick = (fileId: string) => {
    setFileToDelete(fileId);
  };

  const handleConfirmDelete = async () => {
    if (!fileToDelete) return;

    setIsDeleting(true);
    try {
      const response = await fetch(API_ENDPOINTS.ENCOUNTER_CONSENT.DELETE(encounter.id, fileToDelete), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setForms(data);
        onUpdate();
        setAlertMessage({ title: 'Success', message: 'Form deleted successfully', type: 'success' });
      } else {
        setAlertMessage({ title: 'Error', message: 'Failed to delete form', type: 'error' });
      }
    } catch (error) {
      console.error('Error deleting form:', error);
      setAlertMessage({ title: 'Error', message: 'An error occurred', type: 'error' });
    } finally {
      setIsDeleting(false);
      setFileToDelete(null);
    }
  };

  const formatFileSize = (bytes: number | undefined) => {
    if (bytes === undefined || bytes === null || isNaN(bytes)) return 'Unknown size';
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-gray-900">Client Consent Forms</h2>
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
        >
          {isUploading ? (
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
          ) : (
            <Upload className="w-5 h-5 mr-2" />
          )}
          Upload PDF
        </button>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileUpload}
          accept="application/pdf"
          className="hidden"
        />
      </div>

      {uploadError && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-r-md">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <p className="ml-3 text-sm text-red-700">{uploadError}</p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="p-12 flex flex-col items-center justify-center text-gray-500">
            <Loader2 className="w-8 h-8 mb-4 animate-spin text-blue-500" />
            <p>Loading forms...</p>
          </div>
        ) : forms.length === 0 ? (
          <div className="p-16 text-center bg-gray-50">
            <div className="mx-auto h-12 w-12 text-gray-400 mb-4">
              <FileText className="w-full h-full" />
            </div>
            <h3 className="text-lg font-medium text-gray-900">No consent forms yet</h3>
            <p className="text-gray-500 mt-1">Upload a PDF to get started</p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Document</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Signed Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hash</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Uploaded</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {forms.map((form) => (
                <tr key={form.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-blue-50 rounded-lg flex items-center justify-center">
                        <FileText className="w-6 h-6 text-blue-600" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{form.filename}</div>
                        <div className="text-xs text-gray-500">{formatFileSize(form.size_bytes)}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                      ${form.status === 'signed' ? 'bg-green-100 text-green-800' :
                        form.status === 'pending_signature' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'}`}>
                      {form.status === 'signed' && <CheckCircle className="w-3 h-3 mr-1" />}
                      {form.status === 'signed' ? 'Signed' :
                        form.status === 'pending_signature' ? 'Pending Signature' : 'Draft'}
                    </span>
                    {form.signer_info?.name && (
                      <div className="text-xs text-gray-500 mt-1">
                        by {form.signer_info.name}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {form.signed_at ? (
                      <div className="flex flex-col">
                        <span className="text-gray-900 font-medium">{new Date(form.signed_at).toLocaleDateString()}</span>
                        <span className="text-xs text-gray-400">{new Date(form.signed_at).toLocaleTimeString()}</span>
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {form.signature_hash && form.public_token ? (
                      <a
                        href={`/signed-consent/${form.public_token}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center text-xs text-blue-600 hover:text-blue-800 font-mono group relative bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded w-fit transition-colors"
                      >
                        <Hash className="w-3 h-3 mr-1" />
                        <span className="truncate max-w-[100px]">{form.signature_hash.substring(0, 8)}...</span>
                      </a>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(form.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <a
                        href={form.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <ExternalLink className="w-3.5 h-3.5 mr-1.5 text-gray-500" />
                        View
                      </a>

                      {/* Link Generation */}
                      {form.public_token ? (
                        <button
                          onClick={() => copyLinkToClipboard(form.public_token!)}
                          className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          <Copy className="w-3.5 h-3.5 mr-1.5" />
                          Copy Link
                        </button>
                      ) : (
                        <button
                          onClick={() => handleGenerateLink(form.id)}
                          className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          <Link className="w-3.5 h-3.5 mr-1.5 text-gray-500" />
                          Create Link
                        </button>
                      )}

                      <button
                        onClick={() => handleDeleteClick(form.id)}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ConfirmDeleteModal
        isOpen={!!fileToDelete}
        onClose={() => setFileToDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Delete Consent Form"
        message="Are you sure you want to delete this form? This action cannot be undone."
        isDeleting={isDeleting}
      />

      {/* Custom Alert Modal */}
      {alertMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6 transform transition-all">
            <div className="flex items-center mb-4">
              {alertMessage.type === 'success' ? (
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100 sm:mx-0 sm:h-10 sm:w-10">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
              ) : (
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  <AlertCircle className="h-6 w-6 text-red-600" />
                </div>
              )}
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">{alertMessage.title}</h3>
              </div>
            </div>
            <p className="text-sm text-gray-500 mb-6">{alertMessage.message}</p>
            <button
              onClick={() => setAlertMessage(null)}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsentFormsSection;
